import { type FC, useEffect, useState } from 'react'

import {
  agentImage,
  dialingInfoText,
  dialingTopSection,
} from '@liveHelper/components/Dialing/Dialing.twind'
import { AgentImageFallback } from '@liveHelper/components/Idle/Idle.styles'
import { DialingFooter } from '@liveHelper/components/shared/Dialing/DialingFooter'
import { DialingHeader } from '@liveHelper/components/shared/Dialing/DialingHeader'
import { QueuePosition } from '@liveHelper/components/shared/QueuePosition'
import { TripleDotLoader } from '@liveHelper/components/shared/TripleDotLoader'
import { DEFAULT_AGENT_THUMBNAIL } from '@liveHelper/constants'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useTopLevelUIState } from '@liveHelper/hooks/useTopLevelUIState'
import {
  actionsSelector,
  queuePositionSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import { DIALING_CONTAINER } from '@liveHelper/testIds'

import { Image } from '@core/components/Image'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

import { DialingContainer, DialingText, TopSection } from './Dialing.styles'

/** Live helper widget "dialing_minimized" state component. */
export const Dialing: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const queuePosition = useStore(queuePositionSelector)
  const uiState = useStore(uiStateSelector)

  const topLevelUIState = useTopLevelUIState()
  const chatChannelConfig = useChatChannelConfig()
  const isMobileWebEnabled = isMobileWeb()

  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | undefined>(
    DEFAULT_AGENT_THUMBNAIL,
  )

  useEffect(() => {
    if (!chatChannelConfig?.widget_style?.agent_avatars?.length) {
      return
    }

    let index = chatChannelConfig?.widget_style.agent_avatars?.length - 1
    function updateAgentAvatarUrl() {
      if (!chatChannelConfig?.widget_style?.agent_avatars?.length) {
        return
      }

      const agentImageUrl =
        chatChannelConfig.widget_style.agent_avatars[index].url
      if (agentImageUrl) {
        setAgentAvatarUrl(agentImageUrl)
      } else {
        setAgentAvatarUrl(DEFAULT_AGENT_THUMBNAIL)
      }

      index = index - 1
      if (index < 0) {
        index = chatChannelConfig.widget_style.agent_avatars.length - 1
      }
    }

    updateAgentAvatarUrl()
    setInterval(updateAgentAvatarUrl, 5000)
  }, [chatChannelConfig?.widget_style?.agent_avatars])

  if (
    queuePosition &&
    topLevelUIState === 'minimized' &&
    uiState === 'dialing_minimized'
  ) {
    return <QueuePosition queuePosition={queuePosition} />
  }

  return (
    <DialingContainer
      topLevelUIState={topLevelUIState}
      data-testid={DIALING_CONTAINER}
      onClick={() => sendUIStateTransitionEvent('EXPAND')}
      isMobileWeb={isMobileWebEnabled}
    >
      <DialingHeader />
      <TopSection isMobileWeb={isMobileWebEnabled}>
        {uiState === 'dialing_expanded' ? (
          <div
            className={tw?.(
              dialingTopSection,
              'flex',
              'flex-col',
              'items-center',
              'mx-5',
              'py-4',
              'px-6',
              'rounded-2xl',
              'my-4',
            )}
          >
            <div className={tw?.('rounded-full', 'mb-7', 'overflow-hidden')}>
              <Image
                src={agentAvatarUrl}
                className={`chromatic-ignore ${tw?.(agentImage)}`}
                alt={t(`oneToOne:Agent picture`)}
                fallbackComponent={<AgentImageFallback />}
                width="88px"
                height="88px"
              />
            </div>
            <div
              className={tw?.(
                dialingInfoText,
                'text-lg',
                'font-bold',
                'text-center',
              )}
            >
              {t('oneToOne:We will be right with you')}
            </div>
            <div
              className={tw?.(
                dialingInfoText,
                'text-sm',
                'font-medium',
                'mt-3',
                'text-center',
              )}
            >
              {t(
                `oneToOne:You'll see and hear your product expert, but they'll only be able to hear you, not see you.`,
              )}
            </div>
          </div>
        ) : (
          <>
            <DialingText className={tw?.('text-sm')}>
              {t(`oneToOne:Dialing`)}
            </DialingText>
            <TripleDotLoader />
          </>
        )}
        <DialingFooter />
      </TopSection>
    </DialingContainer>
  )
}
